'use client';
import { motion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import Link from 'next/link';
import { useI18n } from '@/i18n';

// Company logo SVGs (simplified representations)
const companyLogos: Record<string, React.ReactNode> = {
  'Logic Utilities': (
    <svg viewBox="0 0 120 24" fill="currentColor" className="h-5 opacity-60">
      <path d="M0 4h4v16H0V4zm8 0h4v12h8v4H8V4zm24 0h12v4H36v4h8v4h-8v4h12v4H32V4zm18 0h12a4 4 0 014 4v12a4 4 0 01-4 4H50V4zm4 4v12h8V8h-8zm22-4h4v20h-4V4zm8 0h16v4H98v4h12v4H98v4h14v4H94V4z" />
    </svg>
  ),
  'Major UAE Telecom': (
    <svg viewBox="0 0 100 24" fill="currentColor" className="h-5 opacity-60">
      <circle cx="12" cy="12" r="10" />
      <path d="M28 4h4v20h-4V4zm8 0h16v4H40v12h12v4H36V4zm24 0h4l8 12V4h4v20h-4l-8-12v12h-4V4zm24 0h12v4H92v4h8v4h-8v4h12v4H88V4z" />
    </svg>
  ),
  'Regional Bank': (
    <svg viewBox="0 0 80 24" fill="currentColor" className="h-5 opacity-60">
      <rect x="0" y="4" width="20" height="16" rx="2" />
      <path d="M26 4h12a4 4 0 014 4v2a4 4 0 01-4 4h-8v6h-4V4zm4 6h8V8h-8v2zM48 4h12a4 4 0 014 4v8a4 4 0 01-4 4H48V4zm4 4v8h8v-8h-8z" />
    </svg>
  ),
};

// Metric badges - populated from translations in component
let metricBadges: { value: string; label: string }[] = [];

function QuoteMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M18 24c0-6.627-5.373-12-12-12v8c2.21 0 4 1.79 4 4v12h12V24zM42 24c0-6.627-5.373-12-12-12v8c2.21 0 4 1.79 4 4v12h12V24z"
        fill="currentColor"
      />
    </svg>
  );
}

// Featured hero testimonial component
function HeroTestimonial({
  testimonial,
}: {
  testimonial: {
    quote: string;
    author: string;
    role: string;
    company: string;
  };
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  return (
    <motion.div
      ref={ref}
      style={{ y, opacity }}
      className="relative col-span-full lg:col-span-8 row-span-2"
    >
      {/* Main card */}
      <div className="relative h-full rounded-[2.5rem] bg-gradient-to-br from-primary/[0.08] via-transparent to-primary/[0.04] border border-primary/10 p-10 sm:p-14 overflow-hidden">
        {/* Floating accent shapes */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />

        {/* Large quote mark */}
        <QuoteMark className="absolute top-8 left-8 w-24 h-24 text-primary/10" />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full justify-between">
          <div>
            {/* Quote */}
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-2xl sm:text-3xl lg:text-4xl font-display text-foreground leading-snug tracking-tight mb-10"
            >
              {testimonial.quote}
            </motion.blockquote>

            {/* Metric highlight */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20"
            >
              <span className="text-2xl font-display font-medium text-primary">
                {metricBadges[0].value}
              </span>
              <span className="text-sm text-primary/80 font-medium">
                {metricBadges[0].label}
              </span>
            </motion.div>
          </div>

          {/* Author section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="flex items-center justify-between mt-10 pt-8 border-t border-primary/10"
          >
            <div className="flex items-center gap-5">
              {/* Avatar with gradient ring */}
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary to-primary/50 blur-sm scale-110 opacity-40" />
                <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center ring-4 ring-background">
                  <span className="text-xl font-display font-medium text-primary-foreground">
                    {testimonial.author.charAt(0)}
                  </span>
                </div>
              </div>

              <div>
                <div className="text-lg font-medium text-foreground">
                  {testimonial.author}
                </div>
                <div className="text-muted-foreground">
                  {testimonial.role}
                </div>
              </div>
            </div>

            {/* Company logo */}
            <div className="hidden sm:block">
              {companyLogos[testimonial.company] || (
                <span className="text-sm font-medium text-muted-foreground">
                  {testimonial.company}
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

// Compact testimonial card
function CompactCard({
  testimonial,
  index,
  metricIndex,
}: {
  testimonial: {
    quote: string;
    author: string;
    role: string;
    company: string;
  };
  index: number;
  metricIndex: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 * (index + 1), duration: 0.6 }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className="group relative rounded-3xl bg-card border border-border p-8 h-full overflow-hidden"
    >
      {/* Hover gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Quote mark */}
        <QuoteMark className="w-10 h-10 text-primary/20 mb-4" />

        {/* Quote */}
        <blockquote className="text-foreground leading-relaxed mb-6 flex-1">
          "{testimonial.quote}"
        </blockquote>

        {/* Metric pill */}
        <div className="inline-flex self-start items-center gap-2 px-3 py-1.5 rounded-full bg-muted mb-6">
          <span className="text-sm font-semibold text-primary">
            {metricBadges[metricIndex].value}
          </span>
          <span className="text-xs text-muted-foreground">
            {metricBadges[metricIndex].label}
          </span>
        </div>

        {/* Author */}
        <div className="flex items-center gap-4 pt-6 border-t border-border">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center group-hover:from-primary/20 group-hover:to-primary/10 transition-colors duration-500">
            <span className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors duration-500">
              {testimonial.author.charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-foreground truncate">
              {testimonial.author}
            </div>
            <div className="text-sm text-muted-foreground truncate">
              {testimonial.role}, {testimonial.company}
            </div>
          </div>
        </div>
      </div>

      {/* Corner accent */}
      <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-primary/5 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </motion.div>
  );
}

// Stats row component
function StatsRow() {
  const { t } = useI18n();
  const sr = t.testimonials.statsRow;
  const stats = [
    { value: sr.agentsMonitored.value, label: sr.agentsMonitored.label, suffix: '' },
    { value: sr.evaluations.value, label: sr.evaluations.label, suffix: '' },
    { value: sr.customerSatisfaction.value, label: sr.customerSatisfaction.label, suffix: sr.customerSatisfaction.suffix },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.3 }}
      className="col-span-full lg:col-span-4 rounded-3xl bg-gradient-to-br from-foreground to-foreground/90 p-8 flex flex-col justify-center"
    >
      <div className="space-y-6">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 + i * 0.1 }}
            className="flex items-baseline justify-between"
          >
            <span className="text-sm text-background/60 font-medium">
              {stat.label}
            </span>
            <span className="text-2xl font-display font-medium text-background">
              {stat.value}
              {stat.suffix && (
                <span className="text-primary-foreground/60">{stat.suffix}</span>
              )}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Decorative line */}
      <div className="mt-8 pt-6 border-t border-background/10">
        <p className="text-sm text-background/50">
          {sr.trustedBy}
        </p>
      </div>
    </motion.div>
  );
}

// Logo carousel component
function LogoMarquee() {
  const logos = Object.entries(companyLogos);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay: 0.5 }}
      className="col-span-full mt-4"
    >
      <div className="relative overflow-hidden py-6">
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-background to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-background to-transparent z-10" />

        {/* Marquee track */}
        <motion.div
          animate={{ x: [0, -50 * logos.length * 3] }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
          className="flex gap-16 items-center"
        >
          {/* Triple the logos for seamless loop */}
          {[...logos, ...logos, ...logos, ...logos, ...logos, ...logos].map(
            ([name, svg], i) => (
              <div
                key={`${name}-${i}`}
                className="flex-shrink-0 grayscale hover:grayscale-0 transition-all duration-300"
              >
                {svg}
              </div>
            )
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

export function Testimonials() {
  const { t } = useI18n();
  const testimonials = t.testimonials.items;
  const sectionRef = useRef<HTMLElement>(null);

  // Populate metric badges from translations
  metricBadges = [
    t.testimonials.metrics.timeSaved,
    t.testimonials.metrics.accuracy,
    t.testimonials.metrics.faster,
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-24 sm:py-32 lg:py-40 overflow-hidden"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 pattern-dots opacity-50" />

      {/* Floating accent orbs */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl mb-16 lg:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">
              {t.testimonials.badge}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-display font-medium text-foreground tracking-tight mb-6"
          >
            {t.testimonials.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg sm:text-xl text-muted-foreground leading-relaxed"
          >
            {t.testimonials.subtitle}
          </motion.p>
        </div>

        {/* Bento grid layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Hero testimonial - spans 8 columns, 2 rows */}
          <HeroTestimonial testimonial={testimonials[0]} />

          {/* Stats card - spans 4 columns */}
          <StatsRow />

          {/* Compact cards - each spans 6 columns on medium, 4 on large */}
          {testimonials.slice(1).map((testimonial, index) => (
            <div key={index} className="lg:col-span-6">
              <CompactCard
                testimonial={testimonial}
                index={index}
                metricIndex={(index + 1) % metricBadges.length}
              />
            </div>
          ))}
        </div>

        {/* Logo marquee */}
        <LogoMarquee />

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-muted-foreground mb-6">
            {t.testimonials.cta.joinMessage}
          </p>
          <Link href="/register">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-foreground text-background font-medium hover:bg-foreground/90 transition-colors"
            >
              {t.testimonials.cta.startTrial}
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
